import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { fmtMoney, fmtDate } from "@/lib/format";
import BranchFilter from "@/components/BranchFilter";
import DateRangeFilter from "@/components/DateRangeFilter";
import useBranches from "@/hooks/useBranches";
import { Plus, MagnifyingGlass, Trash, X } from "@phosphor-icons/react";
import PartPicker from "@/components/PartPicker";

/**
 * Spares tab under Service — the four ledger flavours per user brief.
 * JC issuance / warranty rows are read-only here (they're created inside
 * the Job Card drawer). Inward and Outward transfers are create-able here.
 */
const SUB_TABS = [
  { id: "jc_issuance", label: "JC Spare Issuance", createable: false },
  { id: "jc_warranty", label: "JC Warranty Issuance", createable: false },
  { id: "inward", label: "Spares Inward Invoice", createable: true },
  { id: "outward_transfer", label: "Spares Outward Transfers", createable: true },
];

const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

export default function SparesTab({ canCreate, canDelete }) {
  const [sub, setSub] = useState("jc_issuance");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [branchId, setBranchId] = useState("");
  const [search, setSearch] = useState("");
  const [dateRange, setDateRange] = useState(() => {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const start = new Date(today); start.setDate(today.getDate() - 29);
    return { from: ymd(start), to: ymd(today) };
  });
  const [drawer, setDrawer] = useState(false);

  const currentSub = SUB_TABS.find((s) => s.id === sub);

  const load = async () => {
    setLoading(true);
    try {
      const params = { movement_type: sub };
      if (branchId) params.branch_id = branchId;
      if (dateRange.from) params.date_from = dateRange.from;
      if (dateRange.to) params.date_to = dateRange.to;
      if (search.trim()) params.search = search.trim();
      const { data } = await api.get("/spare-movements", { params });
      setRows(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sub, branchId, dateRange.from, dateRange.to]);

  const totals = useMemo(() => rows.reduce(
    (acc, r) => ({
      count: acc.count + 1,
      qty: acc.qty + (Number(r.total_qty) || 0),
      value: acc.value + (Number(r.total_value) || 0),
    }),
    { count: 0, qty: 0, value: 0 },
  ), [rows]);

  const onDelete = async (row) => {
    if (!window.confirm(`Delete this movement (₹${row.total_value})? This is permanent.`)) return;
    try {
      await api.delete(`/spare-movements/${row.id}`);
      toast.success("Movement deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <>
      <div className="inline-flex border border-canvas-line" data-testid="spares-sub-tabs">
        {SUB_TABS.map((t, i) => (
          <button key={t.id} type="button" onClick={() => setSub(t.id)}
            data-testid={`spares-sub-tab-${t.id}`}
            className={`px-3 py-2 text-[11px] font-mono uppercase tracking-wider ${i > 0 ? "border-l border-canvas-line" : ""} ${sub === t.id ? "bg-ink text-white" : "text-ink hover:bg-canvas-panel"}`}>
            {t.label}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <BranchFilter value={branchId} onChange={setBranchId} testId="spares-branch-filter" kind="service" />
        <DateRangeFilter value={dateRange} onChange={setDateRange} testIdPrefix="spares-date" />
        <div className="flex items-center border border-canvas-line bg-canvas">
          <MagnifyingGlass size={12} weight="bold" className="mx-2 text-ink-muted" />
          <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="Search JC / vendor / part"
            data-testid="spares-search"
            className="px-1 py-2 bg-canvas text-sm focus:outline-none w-64" />
        </div>
        {currentSub.createable && canCreate && (
          <button type="button" onClick={() => setDrawer(true)}
            data-testid={`new-${sub}`}
            className="ml-auto inline-flex items-center gap-2 px-3 py-2 bg-ink text-white text-xs font-mono uppercase tracking-wider hover:bg-ink/90">
            <Plus size={12} weight="bold" /> New {sub === "inward" ? "Inward Invoice" : "Outward Transfer"}
          </button>
        )}
      </div>

      <div className="grid grid-cols-3 gap-2">
        <Kpi label="Movements" value={totals.count} />
        <Kpi label="Total Qty" value={totals.qty} />
        <Kpi label="Total ₹" value={fmtMoney(totals.value)} />
      </div>

      <div className="border border-canvas-line bg-canvas-card overflow-x-auto">
        <table className="w-full text-sm" data-testid="spare-movements-table">
          <thead className="bg-canvas-panel">
            <tr className="text-left font-mono text-[10px] uppercase tracking-[0.18em] text-ink-muted">
              <th className="px-3 py-2.5">Date</th>
              {sub === "jc_issuance" || sub === "jc_warranty" ? (
                <th className="px-3 py-2.5">JC #</th>
              ) : sub === "inward" ? (
                <>
                  <th className="px-3 py-2.5">Vendor</th>
                  <th className="px-3 py-2.5">Vendor Inv #</th>
                </>
              ) : (
                <th className="px-3 py-2.5">Destination</th>
              )}
              <th className="px-3 py-2.5">Parts</th>
              <th className="px-3 py-2.5 text-right">Qty</th>
              <th className="px-3 py-2.5 text-right">₹</th>
              <th className="px-3 py-2.5 w-16"></th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={7} className="px-3 py-10 text-center text-ink-subtle font-mono text-xs">Loading…</td></tr>}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={7} className="px-3 py-10 text-center text-ink-subtle font-mono text-xs">
                No movements in this window. Try widening the date range or clearing filters.
              </td></tr>
            )}
            {!loading && rows.map((r) => (
              <tr key={r.id} data-testid={`movement-row-${r.id}`} className="border-t border-canvas-line hover:bg-canvas-panel/40">
                <td className="px-3 py-2.5 whitespace-nowrap">{fmtDate(r.date)}</td>
                {sub === "jc_issuance" || sub === "jc_warranty" ? (
                  <td className="px-3 py-2.5 font-mono text-xs">{r.jc_no || "—"}</td>
                ) : sub === "inward" ? (
                  <>
                    <td className="px-3 py-2.5 font-medium">{r.vendor_name || "—"}</td>
                    <td className="px-3 py-2.5 font-mono text-xs text-ink-muted">{r.vendor_invoice_no || "—"}</td>
                  </>
                ) : (
                  <td className="px-3 py-2.5 text-xs text-ink-muted">{r.destination_branch_id?.slice(0, 8) || "—"}</td>
                )}
                <td className="px-3 py-2.5 text-xs">
                  {(r.lines || []).slice(0, 3).map((l, i) => (
                    <div key={i} className="truncate max-w-xs">
                      <span className="font-mono text-[10px]">{l.part_code}</span>
                      <span className="text-ink-muted"> · {l.description || ""}</span>
                    </div>
                  ))}
                  {(r.lines || []).length > 3 && (
                    <div className="text-[10px] text-ink-subtle">+ {r.lines.length - 3} more…</div>
                  )}
                </td>
                <td className="px-3 py-2.5 text-right tabular-nums text-xs">{r.total_qty}</td>
                <td className="px-3 py-2.5 text-right tabular-nums font-bold">{fmtMoney(r.total_value)}</td>
                <td className="px-3 py-2.5">
                  {canDelete && (
                    <button type="button" onClick={() => onDelete(r)}
                      data-testid={`delete-movement-${r.id}`}
                      className="p-1.5 border border-canvas-line hover:border-destructive text-destructive" title="Delete">
                      <Trash size={12} weight="bold" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {drawer && (
        <MovementDrawer type={sub} onClose={() => setDrawer(false)} onSaved={() => { setDrawer(false); load(); }} />
      )}
    </>
  );
}

function Kpi({ label, value }) {
  return (
    <div className="border border-canvas-line bg-canvas-card p-3">
      <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted">{label}</div>
      <div className="font-display text-lg font-bold tabular-nums mt-1">{value}</div>
    </div>
  );
}

function MovementDrawer({ type, onClose, onSaved }) {
  const { branches: serviceBranches } = useBranches({ activeOnly: true, kind: "service" });
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    date: new Date().toISOString().slice(0, 10),
    branch_id: "",
    vendor_name: "",
    vendor_invoice_no: "",
    destination_branch_id: "",
    reference: "",
    notes: "",
  });
  const [lines, setLines] = useState([{ part_code: "", description: "", qty: 1, unit_price: 0, tax_percent: 0 }]);

  useEffect(() => {
    if (serviceBranches.length === 1 && !form.branch_id) {
      setForm((f) => ({ ...f, branch_id: serviceBranches[0].id }));
    }
  }, [serviceBranches, form.branch_id]);

  const updateLine = (i, k, v) => setLines((ls) => ls.map((l, ix) => ix === i ? { ...l, [k]: v } : l));
  const addLine = () => setLines((ls) => [...ls, { part_code: "", description: "", qty: 1, unit_price: 0, tax_percent: 0 }]);
  const removeLine = (i) => setLines((ls) => ls.filter((_, ix) => ix !== i));
  const lookupPart = async (i, code) => {
    if (!code.trim()) return;
    try {
      const { data } = await api.get("/spare-parts", { params: { search: code.trim() } });
      const hit = (data || []).find((p) => p.part_code === code.trim());
      if (hit) {
        updateLine(i, "description", hit.description || "");
        updateLine(i, "unit_price", hit.unit_price || 0);
        updateLine(i, "tax_percent", hit.tax_percent || 0);
      }
    } catch (err) { /* non-fatal */ }
  };

  const onSave = async () => {
    if (!form.branch_id) { toast.error("Pick the source branch."); return; }
    if (type === "inward" && !form.vendor_name.trim()) { toast.error("Vendor name is required."); return; }
    if (type === "outward_transfer" && !form.destination_branch_id) { toast.error("Pick a destination branch."); return; }
    const nonEmpty = lines.filter((l) => l.part_code && Number(l.qty) > 0);
    if (nonEmpty.length === 0) { toast.error("Add at least one part with qty > 0."); return; }
    setSaving(true);
    try {
      await api.post("/spare-movements", { movement_type: type, ...form, lines: nonEmpty });
      toast.success(`${type === "inward" ? "Inward invoice" : "Transfer"} recorded`);
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-ink/40" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
           className="w-[720px] bg-canvas-card border-l-2 border-ink overflow-y-auto"
           data-testid="movement-drawer">
        <div className="px-6 py-4 border-b border-canvas-line flex items-center justify-between">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-ink-muted">
              {type === "inward" ? "Spares Inward" : "Spares Outward Transfer"}
            </div>
            <h2 className="font-display text-xl font-bold text-ink mt-1">
              {type === "inward" ? "New Inward Invoice" : "New Transfer"}
            </h2>
          </div>
          <button type="button" onClick={onClose} className="p-2 border border-canvas-line hover:border-ink">
            <X size={14} weight="bold" />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-1">Date</label>
              <input type="date" value={form.date} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))}
                className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm focus:outline-none focus:border-ink" />
            </div>
            <div>
              <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-1">
                {type === "outward_transfer" ? "Source Branch *" : "Branch *"}
              </label>
              <select value={form.branch_id} onChange={(e) => setForm((f) => ({ ...f, branch_id: e.target.value }))}
                data-testid="mov-branch"
                className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm focus:outline-none focus:border-ink">
                <option value="">— select service branch —</option>
                {serviceBranches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </div>
            {type === "inward" && (
              <>
                <div>
                  <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-1">Vendor *</label>
                  <input value={form.vendor_name} onChange={(e) => setForm((f) => ({ ...f, vendor_name: e.target.value }))}
                    data-testid="mov-vendor"
                    className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm focus:outline-none focus:border-ink" />
                </div>
                <div>
                  <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-1">Vendor Invoice #</label>
                  <input value={form.vendor_invoice_no} onChange={(e) => setForm((f) => ({ ...f, vendor_invoice_no: e.target.value }))}
                    data-testid="mov-vendor-inv"
                    className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm font-mono focus:outline-none focus:border-ink" />
                </div>
              </>
            )}
            {type === "outward_transfer" && (
              <div className="col-span-2">
                <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-1">Destination Branch *</label>
                <select value={form.destination_branch_id} onChange={(e) => setForm((f) => ({ ...f, destination_branch_id: e.target.value }))}
                  data-testid="mov-dest"
                  className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm focus:outline-none focus:border-ink">
                  <option value="">— select destination —</option>
                  {serviceBranches.filter((b) => b.id !== form.branch_id).map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              </div>
            )}
          </div>

          <div className="border border-canvas-line">
            <table className="w-full text-xs">
              <thead className="bg-canvas-panel">
                <tr className="text-left font-mono text-[9px] uppercase tracking-wider text-ink-muted">
                  <th className="px-2 py-1 w-24">Part Code</th>
                  <th className="px-2 py-1">Description</th>
                  <th className="px-2 py-1 w-14 text-right">Qty</th>
                  <th className="px-2 py-1 w-16 text-right">Price</th>
                  <th className="px-2 py-1 w-12 text-right">Tax%</th>
                  <th className="px-2 py-1 w-6"></th>
                </tr>
              </thead>
              <tbody>
                {lines.map((l, i) => (
                  <tr key={i} className="border-t border-canvas-line">
                    <td className="px-1 py-1">
                      <PartPicker
                        value={l.part_code}
                        onTypedValue={(v) => updateLine(i, "part_code", v)}
                        onSelect={(p) => {
                          setLines((ls) => ls.map((ln, ix) => ix === i ? {
                            ...ln,
                            part_code: p.part_code,
                            description: p.description || "",
                            unit_price: p.unit_price || 0,
                            tax_percent: p.tax_percent || 0,
                          } : ln));
                        }}
                        testId={`mov-line-part-${i}`}
                      />
                    </td>
                    <td className="px-1 py-1">
                      <input value={l.description} onChange={(e) => updateLine(i, "description", e.target.value)}
                        className="w-full px-1 py-0.5 bg-canvas border border-canvas-line text-xs focus:outline-none focus:border-ink" />
                    </td>
                    <td className="px-1 py-1"><input type="number" value={l.qty} onChange={(e) => updateLine(i, "qty", e.target.value)}
                      className="w-full px-1 py-0.5 bg-canvas border border-canvas-line text-xs text-right tabular-nums focus:outline-none focus:border-ink" /></td>
                    <td className="px-1 py-1"><input type="number" value={l.unit_price} onChange={(e) => updateLine(i, "unit_price", e.target.value)}
                      className="w-full px-1 py-0.5 bg-canvas border border-canvas-line text-xs text-right tabular-nums focus:outline-none focus:border-ink" /></td>
                    <td className="px-1 py-1"><input type="number" value={l.tax_percent} onChange={(e) => updateLine(i, "tax_percent", e.target.value)}
                      className="w-full px-1 py-0.5 bg-canvas border border-canvas-line text-xs text-right tabular-nums focus:outline-none focus:border-ink" /></td>
                    <td className="px-1 py-1">
                      <button type="button" onClick={() => removeLine(i)} className="p-0.5 text-destructive"><Trash size={10} weight="bold" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="px-2 py-1 border-t border-canvas-line">
              <button type="button" onClick={addLine} className="text-[11px] font-mono uppercase tracking-wider text-ink-muted hover:text-ink">
                + Add line
              </button>
            </div>
          </div>

          <div className="pt-3 border-t border-canvas-line flex justify-end gap-2">
            <button type="button" onClick={onClose} className="px-3 py-2 border border-canvas-line text-sm">Cancel</button>
            <button type="button" onClick={onSave} disabled={saving}
              data-testid="save-movement-drawer"
              className="px-4 py-2 bg-ink text-white text-sm font-semibold hover:bg-ink/90 disabled:opacity-60">
              {saving ? "Saving…" : "Save movement"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
